import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiErrorDetail } from "../lib/api";
import { Clock, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import TimeTrackLogo from "../components/TimeTrackLogo";

export default function AcceptInvite() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invites/by-token/${token}`)
      .then((r) => {
        setInvite(r.data);
        setName(r.data.name || "");
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invites/by-token/${token}/accept`, { name, password });
      localStorage.setItem("lom_token", data.access_token);
      setUser(data.user);
      toast.success(`Welcome to ${invite.company_name}!`);
      nav("/home");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-zinc-50 text-zinc-500 font-mono">Loading invite…</div>;
  }

  if (!invite) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 p-6" data-testid="invite-error">
        <div className="max-w-md w-full bg-white border border-zinc-200 p-8 rounded-sm text-center">
          <div className="label-eyebrow text-red-600 mb-2">Invite unavailable</div>
          <h1 className="font-heading font-black text-3xl text-zinc-950 mb-4">Can't open this invite</h1>
          <p className="text-zinc-600">{error || "Invite is missing, expired, or already accepted."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 p-6">
      <div className="max-w-md w-full">
        <div className="mb-8">
          <TimeTrackLogo size={44} variant="lockup" />
        </div>

        <div className="bg-white border border-zinc-200 p-8 rounded-sm" data-testid="invite-card">
          <div className="label-eyebrow mb-2">You're invited to join</div>
          <h1 className="font-heading font-black text-3xl tracking-tighter text-zinc-950 mb-1">{invite.company_name}</h1>
          <p className="text-zinc-600 text-sm mb-6">
            As <span className="font-semibold capitalize text-zinc-950">{invite.role}</span> · <span className="font-mono">{invite.email}</span>
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
            <div>
              <label className="label-eyebrow block mb-2">Your name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 text-sm"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Choose a password</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 text-sm font-mono"
                placeholder="At least 8 characters"
                data-testid="invite-password-input"
              />
            </div>

            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-50 text-white font-semibold rounded-sm transition-colors"
              data-testid="invite-accept-btn"
            >
              <span>{submitting ? "Joining…" : "Accept invite & join"}</span>
              <ArrowRight weight="bold" size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
